import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Row, Button } from "@masterteam/mt-design-react";

import Color from "../Color";
import EditForm from "./EditForm";
import NewTable from "../NewTable";
import CardList from "../CardList";
import AdminPage from "../AdminPage";
import DeleteModal from "../DeleteModal";

import api from "../../providers/api";
import { addToast } from "../../helpers";
import { getThemes, deleteTheme } from "../../reducers/Themes";

import StrategicIcon from "../../assets/images/strategic-strategy.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";

const initialModalState = {
  isOpen: false,
  data: null
};

const ThemesPage = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const { strategies, isLoading } = useSelector(({ Themes }) => Themes);

  const [formModal, setFormModal] = useState(initialModalState);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(getThemes());
  }, []);

  const handleDelete = (id) => {
    setDeleteLoading(true);
    api.theme
      .delete(id)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === "Success") {
          dispatch(deleteTheme({ strategyIndex: selectedIndex, id }));
          setDeleteModal(initialModalState);
          addToast.success("toast.successDelete");
        } else if (StatusCode === "fail") {
          addToast.error(StatusMessage, {
            toastId: "get-users-error"
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error"
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  return (
    <AdminPage title={intl.formatMessage({ id: "common.themes" })}>
      <Row size="large">
        <div style={{ flex: 1 }}>
          <CardList
            showStrategyIcon
            data={strategies}
            title={intl.formatMessage({ id: "common.strategies" })}
            isLoading={isLoading}
            onClickItem={(item) => {
              setSelectedIndex(strategies?.findIndex((e) => e.ID === item.ID));
            }}
            nameKey={isAr ? "ArabicName" : "EnglishName"}
            valueKey="ID"
            selectedItem={strategies?.[selectedIndex]?.ID}
            animation={isAr ? "fadeInRight" : "fadeInLeft"}
          />
        </div>
        <div
          className="animated fadeInRight"
          style={{ flex: 2, height: "calc(100vh - 350px)" }}
        >
          <NewTable
            data={strategies[selectedIndex]?.Themes}
            isLoading={isLoading}
            actionsHeader={
              <Button
                fixed
                onClick={() => setFormModal({ isOpen: true, data: null })}
              >
                {intl.formatMessage({ id: "common.add" })}
              </Button>
            }
            columns={[
              {
                key: "Themes List",
                header: intl.formatMessage({ id: "common.themes" }),
                cell: (item) => (isAr ? item.ArabicName : item.EnglishName),
                width: "50%"
              },
              {
                key: "Color",
                header: intl.formatMessage({ id: "common.color" }),
                cell: (item) => <Color color={item?.Color} />,
                width: "25%"
              },
              {
                key: "Order",
                header: intl.formatMessage({ id: "common.order" }),
                cell: (item) => item?.Order,
                width: "25%"
              }
            ]}
            actions={[
              {
                key: "edit",
                variant: "primary",
                Icon: EditIcon,
                "tooltip-position": "buttom",
                tooltip: intl.formatMessage({ id: "common.update" }),
                onClick: (item) => {
                  setFormModal({ isOpen: true, data: item });
                }
              },
              {
                key: "delete",
                variant: "error",
                Icon: DeleteIcon,
                "tooltip-position": "buttom",
                tooltip: intl.formatMessage({ id: "common.delete" }),
                onClick: (item) => {
                  setDeleteModal({ isOpen: true, data: item.ID });
                },
                disabled: (item) => !item.IsDeletable
              }
            ]}
            compact
            scrollable
          />
        </div>
      </Row>

      {formModal.isOpen && (
        <EditForm
          data={formModal.data}
          selectedStrategy={strategies?.[selectedIndex]}
          closeModal={() => setFormModal(initialModalState)}
        />
      )}

      {deleteModal.isOpen && (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({ id: "common.theme" })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      )}
    </AdminPage>
  );
};

export default ThemesPage;
